import type { ReactNode } from "react";

export interface TabNavItem {
  key: string;
  label: ReactNode;
  href: string;
  active: boolean;
}

export interface TabNavProps {
  /** Accessible name of the navigation landmark, e.g. "Workspace". */
  ariaLabel: string;
  items: TabNavItem[];
  /** Renders each tab. Pass a router Link here so navigation stays client-side. Defaults to a plain anchor. */
  renderLink?: (item: TabNavItem, className: string) => ReactNode;
}

/** Condensed caps tabs: active tab has a 3px blue underline. Mobile scrolls horizontally with an edge fade. */
export const TAB_CLASS =
  "inline-flex min-h-11 items-center whitespace-nowrap border-b-[3px] px-3 font-display text-lg font-bold uppercase tracking-[0.01em] transition-colors duration-[120ms]";

export function tabClassName(active: boolean): string {
  return `${TAB_CLASS} ${active ? "border-blue text-ink" : "border-transparent text-ink-2 hover:text-ink"}`;
}

export function TabNav({ ariaLabel, items, renderLink }: TabNavProps) {
  return (
    <nav aria-label={ariaLabel} className="relative -mx-4 md:mx-0">
      <ul className="flex gap-1 overflow-x-auto border-b border-hairline px-4 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {items.map((item) => {
          const className = tabClassName(item.active);
          return (
            <li key={item.key} className="shrink-0">
              {renderLink ? (
                renderLink(item, className)
              ) : (
                <a
                  href={item.href}
                  className={className}
                  aria-current={item.active ? "page" : undefined}
                >
                  {item.label}
                </a>
              )}
            </li>
          );
        })}
      </ul>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-linear-to-l from-ice to-transparent md:hidden"
      />
    </nav>
  );
}
